(function () {
  'use strict';

  class NbDataPartnersAnimator {
    constructor(section) {
      this.section = section;
      this.body    = section.querySelector('.nb-pdp__body');
      if (!this.body) return;

      this.hardwareItems  = [...section.querySelectorAll('.nb-pdp__hardware-item')];
      this.partnerItems   = [...section.querySelectorAll('.nb-pdp__partner-item')];
      this.centerCard     = section.querySelector('.nb-pdp__platform-card');
      this.diagramEllipse = section.querySelector('.nb-pdp__diagram-ellipse');
      this.hardwareCol    = section.querySelector('.nb-pdp__col--hardware');
      this.partnerCol     = section.querySelector('.nb-pdp__col--partners');

      if (!this.centerCard || !this.hardwareItems.length) return;

      this.svg      = null;
      this.filterId = null;
      this.allPaths = [];
      this.allDots  = [];
      this.startTime = null;
      this.rafId     = null;
      this.destroyed = false;

      // Timing (ms) — both connectors flow inward simultaneously
      this.TRAVEL        = 1400;   // connector bezier travel
      this.RIGHT_STAGGER = 160;    // stagger between right connector dots
      this.LEFT_STAGGER  = 160;    // stagger between left connector dots
      this.CYCLE         = 4500;

      this.mobileMql = window.matchMedia('(max-width: 989px)');
      this._build();
    }

    _isStacked() {
      return this.mobileMql.matches;
    }

    /* ── Setup ─────────────────────────────────────────────────── */

    _build() {
      this.body.style.position = 'relative';

      const uid = 'nb-pdp-' + (this.section.dataset.sectionId || Math.random().toString(36).slice(2));
      this.filterId = uid + '-glow';

      this.svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      this.svg.setAttribute('class', 'nb-pdp__connector-svg');
      this.svg.setAttribute('aria-hidden', 'true');

      const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
      defs.innerHTML = `
        <filter id="${this.filterId}" x="-150%" y="-150%" width="400%" height="400%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="2.5" result="blur"/>
          <feMerge>
            <feMergeNode in="blur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>`;
      this.svg.appendChild(defs);
      this.body.appendChild(this.svg);

      this._drawAllPaths();
      this._createAllDots();

      const ro = new ResizeObserver(() => this._rebuild());
      ro.observe(this.body);

      this.startTime = performance.now();
      this.rafId = requestAnimationFrame(t => this._tick(t));
    }

    /* ── Coordinate helpers ────────────────────────────────────── */

    _rel(el) {
      const br = this.body.getBoundingClientRect();
      const er = el.getBoundingClientRect();
      return {
        left:   er.left   - br.left,
        top:    er.top    - br.top,
        right:  er.right  - br.left,
        bottom: er.bottom - br.top,
        cx:     er.left   - br.left + er.width  / 2,
        cy:     er.top    - br.top  + er.height / 2,
      };
    }

    _bezierD(x1, y1, x2, y2) {
      const mx = x1 + (x2 - x1) * 0.5;
      return `M ${x1} ${y1} C ${mx} ${y1} ${mx} ${y2} ${x2} ${y2}`;
    }

    _bezierV(x1, y1, x2, y2) {
      const my = y1 + (y2 - y1) * 0.5;
      return `M ${x1} ${y1} C ${x1} ${my} ${x2} ${my} ${x2} ${y2}`;
    }

    /* ── Path helpers ──────────────────────────────────────────── */

    _addPath(d, stroke) {
      const el = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      el.setAttribute('d', d);
      el.setAttribute('fill', 'none');
      el.setAttribute('stroke', stroke || 'none');
      if (stroke) el.setAttribute('stroke-width', '1');
      this.svg.appendChild(el);
      this.allPaths.push(el);
      return el;
    }

    _drawAllPaths() {
      const c = this._rel(this.centerCard);

      if (this._isStacked()) {
        // Stacked layout: one shared vertical path per side (hardware→center,
        // center→partner) instead of one path per item, so lines don't cross
        // through the stacked item list.
        const hw = this._rel(this.hardwareCol);
        const pt = this._rel(this.partnerCol);

        const leftShared  = this._addPath(this._bezierV(hw.cx, hw.bottom, c.cx, c.top), '#d5d7da');
        const rightShared = this._addPath(this._bezierV(c.cx, c.bottom, pt.cx, pt.top), '#d5d7da');

        this.leftPaths  = this.hardwareItems.map(() => leftShared);
        this.rightPaths = this.partnerItems.map(() => rightShared);
        return;
      }

      // Phase 1 — right connector paths (drawn center→partner; dots travel reversed)
      this.rightPaths = this.partnerItems.map(item => {
        const p = this._rel(item);
        return this._addPath(this._bezierD(c.right, c.cy, p.left, p.cy), '#d5d7da');
      });

      // Left connector paths (drawn hardware→center; dots travel forward)
      this.leftPaths = this.hardwareItems.map(item => {
        const p = this._rel(item);
        return this._addPath(this._bezierD(p.right, p.cy, c.left, c.cy), '#d5d7da');
      });
    }

    /* ── Dot helpers ───────────────────────────────────────────── */

    _makeDot() {
      const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      g.setAttribute('filter', `url(#${this.filterId})`);
      g.style.opacity    = '0';
      g.style.willChange = 'transform, opacity';

      const halo = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      halo.setAttribute('r',    '6');
      halo.setAttribute('fill', 'rgba(39,96,216,0.28)');

      const core = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      core.setAttribute('r',    '2.5');
      core.setAttribute('fill', '#2760d8');

      g.appendChild(halo);
      g.appendChild(core);
      this.svg.appendChild(g);
      return g;
    }

    _addDot(path, offset, travel, reverse, glowTarget, startGlowTarget) {
      this.allDots.push({
        g:               this._makeDot(),
        path,
        offset,
        travel,
        reverse:         !!reverse,
        glowTarget:      glowTarget      || null,
        glowed:          false,
        startGlowTarget: startGlowTarget || null,
        startGlowed:     false,
      });
    }

    _createAllDots() {
      // Right connectors: partner items → center card (reverse=true; path drawn center→partner)
      this.partnerItems.forEach((item, i) => {
        this._addDot(
          this.rightPaths[i],
          i * this.RIGHT_STAGGER,
          this.TRAVEL,
          true,
          this.centerCard,
          item
        );
      });

      // Left connectors: hardware items → center card (reverse=false; path drawn hardware→center)
      this.hardwareItems.forEach((item, i) => {
        this._addDot(
          this.leftPaths[i],
          i * this.LEFT_STAGGER,
          this.TRAVEL,
          false,
          this.centerCard,
          item
        );
      });
    }

    /* ── Rebuild on resize ─────────────────────────────────────── */

    _rebuild() {
      this.allPaths.forEach(p => p.remove());
      this.allDots.forEach(d => d.g.remove());
      this.allPaths   = [];
      this.allDots    = [];
      this.rightPaths = [];
      this.leftPaths  = [];
      this._drawAllPaths();
      this._createAllDots();
    }

    /* ── Animation loop ────────────────────────────────────────── */

    _tick(timestamp) {
      if (this.destroyed) return;

      const elapsed = (timestamp - this.startTime) % this.CYCLE;

      this.allDots.forEach(dot => {
        if (!dot.path) { dot.g.style.opacity = '0'; return; }

        const t = elapsed - dot.offset;

        if (t <= 0 || t >= dot.travel) {
          dot.g.style.opacity = '0';
          dot.glowed      = false;
          dot.startGlowed = false;
          return;
        }

        const progress = t / dot.travel;
        const len = dot.path.getTotalLength();
        const pt  = dot.path.getPointAtLength(dot.reverse ? (1 - progress) * len : progress * len);

        dot.g.setAttribute('transform', `translate(${pt.x},${pt.y})`);

        const alpha =
          progress < 0.08 ? progress / 0.08 :
          progress > 0.85 ? (1 - progress) / 0.15 : 1;
        dot.g.style.opacity = alpha;

        if (progress <= 0.15 && !dot.startGlowed && dot.startGlowTarget) {
          dot.startGlowed = true;
          this._triggerGlow(dot.startGlowTarget);
        }

        if (progress >= 0.85 && !dot.glowed && dot.glowTarget) {
          dot.glowed = true;
          this._triggerGlow(dot.glowTarget);
        }
      });

      this.rafId = requestAnimationFrame(t => this._tick(t));
    }

    /* ── Glow effect ───────────────────────────────────────────── */

    _triggerGlow(el) {
      if (!el || el.dataset.nbGlowing) return;
      el.dataset.nbGlowing = '1';
      el.classList.add('nb-pdp--glowing');
      setTimeout(() => {
        el.classList.remove('nb-pdp--glowing');
        delete el.dataset.nbGlowing;
      }, 700);
    }

    /* ── Cleanup ───────────────────────────────────────────────── */

    destroy() {
      this.destroyed = true;
      cancelAnimationFrame(this.rafId);
    }
  }

  /* ── Boot ──────────────────────────────────────────────────────── */

  function init() {
    document.querySelectorAll('.nb-platform-data-partners-section').forEach(section => {
      new NbDataPartnersAnimator(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
